/**
 * Pure Path2D math for the sub-element command surface (01-architecture.md §3.1) and
 * headless queries. Every op takes a Path2D and returns a NEW Path2D (or null when the
 * op would produce an invalid path) — commands wrap these in Yjs transactions.
 *
 * CURVES round (docs/bim-plan/11-curves-spike.md §3.1): the offset ops are bulge-aware.
 * An arc edge offsets CONCENTRICALLY (same center, r ∓ δ); miters against neighbors are
 * line∩circle / circle∩circle closed forms with the nearest-root disambiguation, and
 * each arc edge's bulge is RE-DERIVED from its trimmed endpoints about the offset
 * center afterward (a miter trim changes the swept angle, so "bulge unchanged" is only
 * exact for untrimmed ends). The bulge-free fast path is byte-identical to before.
 */
import type { NodeId, Path2D, PathNode, Pt2 } from './types'
import { newNodeId } from './ids'
import {
  BULGE_EPS, bulgeToArc, bulgeFromEndpointsAboutCenter,
  lineCircleIntersect, circleCircleIntersect, type EdgeArc,
} from './arc'

export interface EdgeRef { fromNodeId: NodeId }

const EPS = 1e-9

export const clonePath = (p: Path2D): Path2D => ({
  closed: p.closed,
  nodes: p.nodes.map((n) => ({ ...n, pt: [n.pt[0], n.pt[1]] as Pt2 })),
})

export function nodeIndex(path: Path2D, id: NodeId): number {
  return path.nodes.findIndex((n) => n.id === id)
}

/** Edge i runs nodes[i] -> nodes[i+1] (wrapping when closed). Count of edges. */
export const edgeCount = (p: Path2D): number =>
  p.closed ? p.nodes.length : Math.max(0, p.nodes.length - 1)

/**
 * Reverse a path's traversal direction (JOIN round — needed to re-orient a
 * member so it continues a chain being built the other way). `bulge` lives on
 * the node an edge LEAVES from, so reversing direction both SHIFTS each
 * bulge to the node before it (in the new order) and NEGATES it (a sweep
 * that was CW one way is CCW the other). Node ids/points are otherwise
 * untouched — this is a re-read, not new geometry. Handles both open (the
 * new last node has no outgoing edge, hence no bulge) and closed (every node
 * has one, wrapping) paths.
 */
export function reversePath(path: Path2D): Path2D {
  const ns = path.nodes
  const n = ns.length
  const nodes: PathNode[] = ns.map((_, j) => {
    const src = ns[n - 1 - j]
    const bulgeIdx = path.closed ? (((n - 2 - j) % n) + n) % n : (j < n - 1 ? n - 2 - j : -1)
    const bulgeSrc = bulgeIdx >= 0 ? ns[bulgeIdx].bulge : undefined
    const out: PathNode = { id: src.id, pt: [src.pt[0], src.pt[1]] }
    if (bulgeSrc) out.bulge = -bulgeSrc
    return out
  })
  return { closed: path.closed, nodes }
}

/** Resolve an EdgeRef to [fromIndex, toIndex], or null if it isn't a valid edge. */
export function edgeIndices(path: Path2D, edge: EdgeRef): [number, number] | null {
  const i = nodeIndex(path, edge.fromNodeId)
  if (i < 0) return null
  if (i === path.nodes.length - 1) {
    if (!path.closed) return null
    return [i, 0]
  }
  return [i, i + 1]
}

/** True path length — arc edges measure their ARC length (CURVES round), not the chord. */
export function pathLength(p: Path2D): number {
  let len = 0
  const n = p.nodes.length
  const last = p.closed ? n : n - 1
  for (let i = 0; i < last; i++) {
    const a = p.nodes[i].pt, b = p.nodes[(i + 1) % n].pt
    const arc = bulgeToArc(a, b, p.nodes[i].bulge)
    len += arc ? arc.r * Math.abs(arc.sweep) : Math.hypot(b[0] - a[0], b[1] - a[1])
  }
  return len
}

/**
 * Signed area (closed paths). Convention: POSITIVE = counter-clockwise in Y-up,
 * matching bim-geometry's signedArea — keep the two in lockstep (a flipped sign there
 * inverted wall winding once already). CURVES round: arc edges add their circular-
 * segment area beyond the chord — (r²/2)(θ − sin θ), whose own sign is correct for
 * either winding.
 */
export function pathArea(p: Path2D): number {
  if (!p.closed || p.nodes.length < 3) return 0
  let s = 0
  const n = p.nodes.length
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const a = p.nodes[j].pt, b = p.nodes[i].pt
    s += a[0] * b[1] - b[0] * a[1]
  }
  let seg = 0
  for (let i = 0; i < n; i++) {
    const arc = bulgeToArc(p.nodes[i].pt, p.nodes[(i + 1) % n].pt, p.nodes[i].bulge)
    if (arc) seg += (arc.r * arc.r / 2) * (arc.sweep - Math.sin(arc.sweep))
  }
  return s / 2 + seg
}

// ---------- internal line helpers ----------

interface Line { p: Pt2; d: Pt2 } // point + direction (not normalized requirement)

function lineIntersect(l1: Line, l2: Line): Pt2 | null {
  const cross = l1.d[0] * l2.d[1] - l1.d[1] * l2.d[0]
  if (Math.abs(cross) < EPS) return null
  const dx = l2.p[0] - l1.p[0], dy = l2.p[1] - l1.p[1]
  const t = (dx * l2.d[1] - dy * l2.d[0]) / cross
  return [l1.p[0] + l1.d[0] * t, l1.p[1] + l1.d[1] * t]
}

/** Left-hand normal of edge direction, unit length; null for zero-length edges. */
function leftNormal(a: Pt2, b: Pt2): Pt2 | null {
  const dx = b[0] - a[0], dy = b[1] - a[1]
  const len = Math.hypot(dx, dy)
  if (len < EPS) return null
  return [-dy / len, dx / len]
}

// ---------- bulge-aware offset internals (CURVES round) ----------

/**
 * One edge's offset geometry: a moved LINE for straight edges (today's math exactly),
 * or a moved CIRCLE for arc edges (concentric — the LEFT offset shrinks a CCW arc's
 * radius). `nA`/`nB` are the edge's left normals AT its two endpoints (constant for a
 * line, radial for an arc) — the endpoint-translate fallback directions.
 */
interface OffsetCurve {
  kind: 'line' | 'circle'
  line?: Line
  c?: Pt2
  r?: number
  sweepSign?: 1 | -1
  nA: Pt2
  nB: Pt2
}

/** left normal of TRAVEL at an arc endpoint = −sign(sweep) · radial-out (unit) */
function arcEndNormal(arc: EdgeArc, p: Pt2): Pt2 {
  const dx = p[0] - arc.c[0], dy = p[1] - arc.c[1]
  const len = Math.hypot(dx, dy) || 1
  const s = arc.sweep >= 0 ? -1 : 1
  return [(dx / len) * s, (dy / len) * s]
}

/** Offset geometry for edge a→b (bulge on the from-node) moved `distanceFt` left. Null
 *  for zero-length edges. A concentric offset that would collapse past the center
 *  degrades to the chord line — same graceful family as the near-parallel fallback. */
function offsetCurveFor(a: Pt2, b: Pt2, bulge: number | undefined, distanceFt: number): OffsetCurve | null {
  const arc = bulgeToArc(a, b, bulge)
  if (arc) {
    const sign: 1 | -1 = arc.sweep >= 0 ? 1 : -1
    const rMoved = arc.r - sign * distanceFt
    if (rMoved > EPS) {
      return {
        kind: 'circle', c: [arc.c[0], arc.c[1]], r: rMoved, sweepSign: sign,
        nA: arcEndNormal(arc, a), nB: arcEndNormal(arc, b),
      }
    }
  }
  const n = leftNormal(a, b)
  if (!n) return null
  return {
    kind: 'line',
    line: { p: [a[0] + n[0] * distanceFt, a[1] + n[1] * distanceFt], d: [b[0] - a[0], b[1] - a[1]] },
    nA: n, nB: n,
  }
}

const nearest = (roots: Pt2[], to: Pt2): Pt2 | null => {
  let best: Pt2 | null = null
  let bd = Infinity
  for (const q of roots) {
    const d = Math.hypot(q[0] - to[0], q[1] - to[1])
    if (d < bd) { bd = d; best = q }
  }
  return best
}

/** Intersection of two offset curves — the mitered node position. `near` disambiguates
 *  multi-root cases (the two circle roots / line-circle roots). Null = no hit. */
function curvesIntersect(cIn: OffsetCurve, cOut: OffsetCurve, near: Pt2): Pt2 | null {
  if (cIn.kind === 'line' && cOut.kind === 'line') return lineIntersect(cIn.line!, cOut.line!)
  if (cIn.kind === 'circle' && cOut.kind === 'circle') {
    return nearest(circleCircleIntersect(cIn.c!, cIn.r!, cOut.c!, cOut.r!), near)
  }
  const ln = cIn.kind === 'line' ? cIn.line! : cOut.line!
  const ci = cIn.kind === 'circle' ? cIn : cOut
  return nearest(lineCircleIntersect(ln.p, ln.d, ci.c!, ci.r!), near)
}

/**
 * Post-placement pass: re-derive every arc edge's bulge from its (possibly trimmed)
 * endpoints about the offset circle's center, preserving sweep direction. Edges whose
 * curve degraded to a line keep their stored bulge (the shape re-radiuses through the
 * chord+bulge invariant, the moveNode behavior).
 */
function restoreBulges(out: Path2D, curves: (OffsetCurve | null)[]): void {
  const count = out.nodes.length
  curves.forEach((cv, e) => {
    if (!cv || cv.kind !== 'circle') return
    const a = out.nodes[e].pt, b = out.nodes[(e + 1) % count].pt
    out.nodes[e].bulge = bulgeFromEndpointsAboutCenter(a, b, cv.c!, cv.sweepSign!)
  })
}

// ---------- the six ops ----------

/**
 * Merge per-edge attributes onto the edge LEAVING `edge.fromNodeId` (Phase 3 roofs —
 * PathNode.edge). `null` values delete the attr (revert to the category default);
 * an empty resulting record removes the `edge` field entirely.
 */
export function setEdgeAttrs(
  path: Path2D,
  edge: EdgeRef,
  attrs: Record<string, number | boolean | string | null>,
): Path2D | null {
  const idx = edgeIndices(path, edge)
  if (!idx) return null
  const out = clonePath(path)
  const node = out.nodes[idx[0]]
  const merged: Record<string, number | boolean | string> = { ...(node.edge ?? {}) }
  for (const [k, v] of Object.entries(attrs)) {
    if (v === null) delete merged[k]
    else merged[k] = v
  }
  if (Object.keys(merged).length) node.edge = merged as Path2D['nodes'][number]['edge']
  else delete node.edge
  return out
}

export function moveNode(path: Path2D, id: NodeId, pt: Pt2): Path2D | null {
  const i = nodeIndex(path, id)
  if (i < 0) return null
  const out = clonePath(path)
  out.nodes[i].pt = [pt[0], pt[1]]
  return out
}

/**
 * Insert a new node on `edge` at `pt`. Returns the new path + minted node id.
 * Both halves of a split edge are the SAME design edge: the new node's leaving
 * edge inherits the original's per-edge attrs (roof slopeRise/gable/overhangFt —
 * without this, splitting a roof edge silently reverted its second half to the
 * category's default slope-defining plane, turning e.g. a gable into a hip), and
 * an arc edge splits into two arcs on the ORIGINAL circle (per-half bulges
 * re-derived about the same center, preserving sweep direction).
 */
export function insertNode(path: Path2D, edge: EdgeRef, pt: Pt2): { path: Path2D; nodeId: NodeId } | null {
  const idx = edgeIndices(path, edge)
  if (!idx) return null
  const out = clonePath(path)
  const from = out.nodes[idx[0]]
  const to = out.nodes[idx[1]]
  const node: PathNode = { id: newNodeId(), pt: [pt[0], pt[1]] }
  if (from.edge) node.edge = { ...from.edge }
  const arc = bulgeToArc(from.pt, to.pt, from.bulge)
  if (arc) {
    const sign = Math.sign(arc.sweep)
    from.bulge = bulgeFromEndpointsAboutCenter(from.pt, node.pt, arc.c, sign)
    node.bulge = bulgeFromEndpointsAboutCenter(node.pt, to.pt, arc.c, sign)
  }
  out.nodes.splice(idx[0] + 1, 0, node)
  return { path: out, nodeId: node.id }
}

/** Delete a node. Open paths keep ≥2 nodes, closed keep ≥3 — otherwise null (invalid). */
export function deleteNode(path: Path2D, id: NodeId): Path2D | null {
  const i = nodeIndex(path, id)
  if (i < 0) return null
  const min = path.closed ? 3 : 2
  if (path.nodes.length <= min) return null
  const out = clonePath(path)
  out.nodes.splice(i, 1)
  return out
}

/** Translate both endpoints of an edge by delta (adjacent edges follow their endpoints). */
export function moveEdge(path: Path2D, edge: EdgeRef, delta: Pt2): Path2D | null {
  const idx = edgeIndices(path, edge)
  if (!idx) return null
  const out = clonePath(path)
  for (const i of idx) {
    out.nodes[i].pt = [out.nodes[i].pt[0] + delta[0], out.nodes[i].pt[1] + delta[1]]
  }
  return out
}

/**
 * Offset one edge parallel to itself by `distanceFt` along its LEFT normal (negative =
 * right). Adjacent edges are extended/trimmed: each moved endpoint is the intersection of
 * the moved edge line with the (unmoved) adjacent edge line; path ends and near-parallel
 * neighbors fall back to a plain translate (03-phases.md Phase-1 acceptance).
 */
export function offsetEdge(path: Path2D, edge: EdgeRef, distanceFt: number): Path2D | null {
  const idx = edgeIndices(path, edge)
  if (!idx) return null
  const [i, j] = idx
  const a = path.nodes[i].pt, b = path.nodes[j].pt
  const moved = offsetCurveFor(a, b, path.nodes[i].bulge, distanceFt)
  if (!moved) return null
  const out = clonePath(path)
  const count = path.nodes.length
  // plain endpoint translates (also the no-hit fallback): along the moved edge's own
  // endpoint normals — constant for a line, radial for an arc (an untrimmed arc end
  // moving radially IS the exact concentric offset)
  const fallA: Pt2 = [a[0] + moved.nA[0] * distanceFt, a[1] + moved.nA[1] * distanceFt]
  const fallB: Pt2 = [b[0] + moved.nB[0] * distanceFt, b[1] + moved.nB[1] * distanceFt]

  // new position for node i: intersect the moved curve with the UNMOVED previous edge
  const hasPrev = path.closed || i > 0
  if (hasPrev) {
    const h = (i - 1 + count) % count
    const prev = offsetCurveFor(path.nodes[h].pt, a, path.nodes[h].bulge, 0)
    const hit = prev ? curvesIntersect(prev, moved, fallA) : null
    out.nodes[i].pt = hit ?? fallA
  } else {
    out.nodes[i].pt = fallA
  }

  // new position for node j: intersect the moved curve with the UNMOVED next edge
  const hasNext = path.closed || j < count - 1
  if (hasNext) {
    const k = (j + 1) % count
    const next = offsetCurveFor(b, path.nodes[k].pt, path.nodes[j].bulge, 0)
    const hit = next ? curvesIntersect(moved, next, fallB) : null
    out.nodes[j].pt = hit ?? fallB
  } else {
    out.nodes[j].pt = fallB
  }

  // re-derive trimmed bulges: the moved edge about ITS offset circle; the two unmoved
  // neighbors about their own original circles (their near endpoint slid along them)
  if (moved.kind === 'circle') {
    out.nodes[i].bulge = bulgeFromEndpointsAboutCenter(out.nodes[i].pt, out.nodes[j].pt, moved.c!, moved.sweepSign!)
  }
  const reBulgeNeighbor = (fromIdx: number): void => {
    const toIdx = (fromIdx + 1) % count
    const arc = bulgeToArc(path.nodes[fromIdx].pt, path.nodes[toIdx].pt, path.nodes[fromIdx].bulge)
    if (!arc) return
    out.nodes[fromIdx].bulge = bulgeFromEndpointsAboutCenter(
      out.nodes[fromIdx].pt, out.nodes[toIdx].pt, arc.c, arc.sweep >= 0 ? 1 : -1)
  }
  if (hasPrev) reBulgeNeighbor((i - 1 + count) % count)
  if (hasNext) reBulgeNeighbor(j)
  return out
}

// ---------- rigid transforms (ROTATE + MIRROR round, 2026-08) ----------

/** Rotate one point about `center` by `deg` (CCW positive, matching screen/world convention
 *  used everywhere else in the kernel — atan2/sin/cos in standard math orientation). */
export function rotatePoint(pt: Pt2, center: Pt2, deg: number): Pt2 {
  const rad = (deg * Math.PI) / 180
  const c = Math.cos(rad), s = Math.sin(rad)
  const dx = pt[0] - center[0], dy = pt[1] - center[1]
  return [center[0] + dx * c - dy * s, center[1] + dx * s + dy * c]
}

/** Rotate every node about `center` by `deg` — winding is preserved (rotation is
 *  orientation-preserving), so open and closed paths need no special handling. */
export function rotatePath(path: Path2D, center: Pt2, deg: number): Path2D {
  return { closed: path.closed, nodes: path.nodes.map((n) => ({ ...n, pt: rotatePoint(n.pt, center, deg) })) }
}

/** Reflect one point across the line through `axisOrigin` in direction `axisDir`. */
export function mirrorPoint(pt: Pt2, axisOrigin: Pt2, axisDir: Pt2): Pt2 {
  const len = Math.hypot(axisDir[0], axisDir[1])
  if (len < EPS) return [pt[0], pt[1]]
  const ux = axisDir[0] / len, uy = axisDir[1] / len
  const dx = pt[0] - axisOrigin[0], dy = pt[1] - axisOrigin[1]
  const proj = dx * ux + dy * uy
  const perpX = dx - proj * ux, perpY = dy - proj * uy
  return [pt[0] - 2 * perpX, pt[1] - 2 * perpY]
}

/**
 * Reflect a whole path across the axis (ROTATE + MIRROR round — docs/MODELER.md). A
 * reflection is orientation-REVERSING, so a plain per-node point-flip silently turns a
 * CCW boundary into CW (the offsetAllEdges/roof-overhang "outward" convention keys off
 * that sign) — for a CLOSED path the node order is also reversed to keep winding
 * consistent, and per-edge attrs (roof slope/overhang/gable, keyed on the FROM-node)
 * re-map to the node that now precedes the same PHYSICAL edge in the reversed sequence.
 * Node ids travel with their own reflected position (a dimension witness or any other
 * nodeId reference still means "the same vertex," just moved). OPEN paths (walls, and
 * every hosted-into category) keep node order as-is — arc length from the start node is
 * exactly what host re-anchoring keys on, and reversing it would relocate every hosted
 * opening for no reason.
 */
export function mirrorPath(path: Path2D, axisOrigin: Pt2, axisDir: Pt2): Path2D {
  const mirrored = (pt: Pt2) => mirrorPoint(pt, axisOrigin, axisDir)
  const n = path.nodes.length
  if (!path.closed) {
    // OPEN: node order (and each edge's travel direction) is preserved, so the
    // reflection alone flips every arc's sweep orientation — bulge NEGATES in place
    // (CURVES round, 11-curves-spike.md §3.1).
    return {
      closed: false,
      nodes: path.nodes.map((nd) => ({
        ...nd,
        pt: mirrored(nd.pt),
        ...(nd.bulge !== undefined ? { bulge: -nd.bulge } : {}),
      })),
    }
  }
  const nodes: PathNode[] = new Array(n)
  for (let i = 0; i < n; i++) {
    const src = path.nodes[(n - 1 - i) % n]
    const edgeSrc = path.nodes[(((n - 2 - i) % n) + n) % n]
    // CLOSED: the node-order reversal ALSO reverses each physical edge's travel
    // direction — reversal and reflection each negate the bulge, so the two cancel:
    // the bulge re-maps to the node now preceding the same physical edge (exactly
    // like `edge` attrs) with its VALUE unchanged.
    nodes[i] = {
      id: src.id, pt: mirrored(src.pt),
      ...(edgeSrc.edge ? { edge: edgeSrc.edge } : {}),
      ...(edgeSrc.bulge !== undefined ? { bulge: edgeSrc.bulge } : {}),
    }
  }
  return { closed: true, nodes }
}

/** Set (or clear, at |bulge| < BULGE_EPS) the curvature of the edge LEAVING
 *  `edge.fromNodeId` — the CURVES round's one new stored value. */
export function setBulge(path: Path2D, edge: EdgeRef, bulge: number): Path2D | null {
  const idx = edgeIndices(path, edge)
  if (!idx) return null
  const out = clonePath(path)
  if (Math.abs(bulge) < BULGE_EPS) delete out.nodes[idx[0]].bulge
  else out.nodes[idx[0]].bulge = bulge
  return out
}

// ---------- polyline measure helpers (moved from frontend handleConfigs, 2.6a) ----------

/** Project a world point onto a polyline → { s, dist } (s = along-length in feet). */
export function projectToPolyline(pts: Pt2[], p: Pt2): { s: number; dist: number } {
  let best = { s: 0, dist: Infinity }
  let acc = 0
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i], b = pts[i + 1]
    const dx = b[0] - a[0], dy = b[1] - a[1]
    const len = Math.hypot(dx, dy)
    if (len < 1e-9) continue
    let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / (len * len)
    t = Math.max(0, Math.min(1, t))
    const qx = a[0] + dx * t, qy = a[1] + dy * t
    const dist = Math.hypot(p[0] - qx, p[1] - qy)
    if (dist < best.dist) best = { s: acc + len * t, dist }
    acc += len
  }
  return best
}

/** World point + unit direction at along-length s on a polyline. */
export function polylinePointAt(pts: Pt2[], s: number): { pt: Pt2; dir: Pt2 } {
  let acc = 0
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i], b = pts[i + 1]
    const len = Math.hypot(b[0] - a[0], b[1] - a[1])
    if (len < 1e-9) continue
    if (s <= acc + len || i === pts.length - 2) {
      const t = Math.max(0, Math.min(1, (s - acc) / len))
      return {
        pt: [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t],
        dir: [(b[0] - a[0]) / len, (b[1] - a[1]) / len],
      }
    }
    acc += len
  }
  return { pt: pts[0] ?? [0, 0], dir: [1, 0] }
}

/**
 * Mitered parallel offset of a WHOLE path by `distanceFt` along the left normal
 * (2.6a location lines — docs/bim-plan/06 §4.2). Unlike offsetAllEdges (a user-facing
 * edit op), this is a pure derivation used to compute a wall's centerline from its
 * stored reference line, so it PRESERVES node count and node ids 1:1 (openings/frames
 * key off stored-path topology). Spike miters are clamped: when a mitered intersection
 * would displace a node more than `miterLimit × |distanceFt|`, that node falls back to
 * a plain average-normal translate (no bevel node insertion — a deliberate deviation
 * from a true bevel, recorded in 06, to keep the 1:1 topology guarantee).
 * distance 0 returns a clone (identity).
 */
export function offsetPolyline(path: Path2D, distanceFt: number, miterLimit = 4): Path2D | null {
  const count = path.nodes.length
  if (count < 2) return null
  if (Math.abs(distanceFt) < EPS) return clonePath(path)
  const nEdges = edgeCount(path)
  const curves: (OffsetCurve | null)[] = []
  for (let e = 0; e < nEdges; e++) {
    const a = path.nodes[e].pt, b = path.nodes[(e + 1) % count].pt
    curves.push(offsetCurveFor(a, b, path.nodes[e].bulge, distanceFt))
  }
  const maxDisp = Math.abs(distanceFt) * Math.max(miterLimit, 1)
  const out = clonePath(path)
  for (let i = 0; i < count; i++) {
    const inEdge = path.closed ? (i - 1 + nEdges) % nEdges : i - 1
    const outEdge = path.closed ? i % nEdges : i
    const cIn = inEdge >= 0 && inEdge < nEdges ? curves[inEdge] : null
    const cOut = outEdge >= 0 && outEdge < nEdges ? curves[outEdge] : null
    // endpoint normals AT this node: the in-edge's end normal, the out-edge's start
    // normal (constant for lines, radial for arcs)
    const nIn = cIn ? cIn.nB : null
    const nOut = cOut ? cOut.nA : null
    const orig = path.nodes[i].pt
    // averaged-normal fallback position (also the spike clamp target)
    let fx = 0, fy = 0
    if (nIn) { fx += nIn[0]; fy += nIn[1] }
    if (nOut) { fx += nOut[0]; fy += nOut[1] }
    const flen = Math.hypot(fx, fy)
    const fallback: Pt2 = flen < EPS
      ? [orig[0], orig[1]]
      : [orig[0] + (fx / flen) * distanceFt, orig[1] + (fy / flen) * distanceFt]
    if (cIn && cOut) {
      const hit = curvesIntersect(cIn, cOut, fallback)
      if (hit && Math.hypot(hit[0] - orig[0], hit[1] - orig[1]) <= maxDisp) {
        out.nodes[i].pt = hit
        continue
      }
      out.nodes[i].pt = fallback   // near-parallel (no hit) or spike (miter limit)
      continue
    }
    out.nodes[i].pt = fallback     // open-path end: plain normal translate
  }
  restoreBulges(out, curves)
  return out
}

/**
 * Extend/trim helper (docs/bim-plan/05 §4): the new position for an END node of an OPEN
 * path so that its end segment lands exactly on the (infinite) line through a↔b — one
 * operation covers both extend and trim, and the caller dispatches plain moveNode with
 * the result (no new command; one undo step). Returns null when the ref isn't an end
 * node of an open path, or the lines are near-parallel.
 */
export function adjustEndToLine(path: Path2D, id: NodeId, a: Pt2, b: Pt2): Pt2 | null {
  if (path.closed) return null
  const i = nodeIndex(path, id)
  const last = path.nodes.length - 1
  if (i !== 0 && i !== last) return null
  const neighborIdx = i === 0 ? 1 : last - 1
  const neighbor = path.nodes[neighborIdx].pt
  const end = path.nodes[i].pt
  // CURVES round: an arc END edge extends/trims along its own CIRCLE — line∩circle,
  // nearest root to the current end. (The commit is still a plain moveNode, so the
  // arc re-radiuses through the chord+bulge invariant afterwards — the node-drag rule.)
  const endBulge = path.nodes[i === 0 ? 0 : last - 1].bulge
  const arc = bulgeToArc(
    i === 0 ? end : neighbor,
    i === 0 ? neighbor : end,
    endBulge,
  )
  if (arc) {
    return nearest(lineCircleIntersect(a, [b[0] - a[0], b[1] - a[1]], arc.c, arc.r), end)
  }
  const own: Line = { p: neighbor, d: [end[0] - neighbor[0], end[1] - neighbor[1]] }
  const target: Line = { p: a, d: [b[0] - a[0], b[1] - a[1]] }
  const ownLen = Math.hypot(own.d[0], own.d[1])
  const targetLen = Math.hypot(target.d[0], target.d[1])
  if (ownLen < EPS || targetLen < EPS) return null
  const cross = own.d[0] * target.d[1] - own.d[1] * target.d[0]
  if (Math.abs(cross / (ownLen * targetLen)) < 1e-3) return null // near-parallel: refuse
  return lineIntersect(own, target)
}

/**
 * Offset EVERY edge by `distanceFt` along its left normal (a uniform parallel offset —
 * for a CCW closed boundary, positive = INWARD: left of travel is the interior side.
 * Corrected 2026-08-20, slice C of bim-plan/14 — this line used to claim outward,
 * contradicting leftNormal's actual math). Interior nodes land on the intersection
 * of their two adjacent offset lines (mitered); near-parallel pairs and open-path ends
 * fall back to a plain normal translate.
 */
export function offsetAllEdges(path: Path2D, distanceFt: number): Path2D | null {
  return offsetEdgesVar(path, Array.from({ length: edgeCount(path) }, () => distanceFt))
}

/**
 * Per-edge-distance parallel offset (bim-plan/14 slice C — the setback-envelope
 * derivation: each property-line edge moves by ITS OWN role's setback). Same mitered
 * intersection math as the uniform offset above (this is the shared implementation —
 * `offsetAllEdges` delegates with a constant array): interior nodes land on the
 * intersection of their two adjacent offset curves, arc edges offset concentrically,
 * near-parallel pairs and open-path ends fall back to a plain normal translate at
 * that node's own edge distance. Sign convention unchanged: left of travel — for a
 * CCW closed boundary, positive = INWARD. `distances.length` must equal
 * `edgeCount(path)`.
 */
export function offsetEdgesVar(path: Path2D, distances: number[]): Path2D | null {
  const count = path.nodes.length
  if (count < 2) return null
  const nEdges = edgeCount(path)
  if (distances.length !== nEdges) return null
  const curves: (OffsetCurve | null)[] = []
  for (let e = 0; e < nEdges; e++) {
    const a = path.nodes[e].pt, b = path.nodes[(e + 1) % count].pt
    curves.push(offsetCurveFor(a, b, path.nodes[e].bulge, distances[e]))
  }
  const out = clonePath(path)
  for (let i = 0; i < count; i++) {
    const inEdge = path.closed ? (i - 1 + nEdges) % nEdges : i - 1   // edge arriving at node i
    const outEdge = path.closed ? i % nEdges : i                      // edge leaving node i
    const cIn = inEdge >= 0 && inEdge < nEdges ? curves[inEdge] : null
    const cOut = outEdge >= 0 && outEdge < nEdges ? curves[outEdge] : null
    const orig = path.nodes[i].pt
    // plain endpoint translate along the surviving edge's own normal AT this node —
    // for straight edges this reproduces the old ref-point leftNormal math exactly
    const nrm = cOut ? cOut.nA : cIn ? cIn.nB : null
    const nrmDist = cOut ? distances[outEdge] : cIn ? distances[inEdge] : 0
    const fallback: Pt2 = nrm
      ? [orig[0] + nrm[0] * nrmDist, orig[1] + nrm[1] * nrmDist]
      : [orig[0], orig[1]]
    if (cIn && cOut) {
      const hit = curvesIntersect(cIn, cOut, fallback)
      if (hit) { out.nodes[i].pt = hit; continue }
    }
    if (!cIn && !cOut) return null
    out.nodes[i].pt = fallback
  }
  restoreBulges(out, curves)
  return out
}
